import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { trackOrder, verifyCheckoutPayment } from "../../config/api";
import {
  getPaymentResponse,
  clearCheckoutSession,
  getCheckoutPending,
} from "../../utils/checkoutRecovery";

const POLL_INTERVAL_MS = 5000;
const MAX_POLLS = 24;

const CheckoutPendingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = searchParams.get("token");
  const [token, setToken] = useState(tokenFromUrl);
  const [status, setStatus] = useState("polling");
  const [orderNumber, setOrderNumber] = useState(null);
  const [pollCount, setPollCount] = useState(0);
  const [lastAction, setLastAction] = useState("Checking payment status…");

  const retryVerifyFromSession = useCallback(async () => {
    const payment = getPaymentResponse();
    const pending = getCheckoutPending();
    const activeToken =
      token || tokenFromUrl || payment?.trackingToken || pending?.trackingToken;

    if (activeToken && activeToken !== token) {
      setToken(activeToken);
    }

    if (!payment?.razorpay_payment_id) {
      return false;
    }

    setLastAction("Retrying payment confirmation…");
    try {
      const result = await verifyCheckoutPayment({
        razorpay_order_id: payment.razorpay_order_id,
        razorpay_payment_id: payment.razorpay_payment_id,
        razorpay_signature: payment.razorpay_signature,
      });

      if (result?.success) {
        clearCheckoutSession();
        const resolvedToken =
          result.trackingToken || activeToken || payment.trackingToken;
        const resolvedOrder =
          result.data?.order_number || payment.orderNumber || "";
        navigate(
          `/checkout/success?token=${resolvedToken}&order=${encodeURIComponent(resolvedOrder)}`,
          { replace: true },
        );
        return true;
      }
    } catch {
      // Fall through to status polling / webhook backup
    }
    return false;
  }, [token, tokenFromUrl, navigate]);

  const checkStatus = useCallback(async () => {
    const payment = getPaymentResponse();
    const pending = getCheckoutPending();
    const activeToken =
      token || tokenFromUrl || payment?.trackingToken || pending?.trackingToken;

    if (!activeToken) {
      setStatus("no_token");
      return false;
    }

    if (activeToken !== token) {
      setToken(activeToken);
    }

    setLastAction("Checking order status…");
    try {
      const result = await trackOrder(activeToken);
      if (result.success && result.data) {
        setOrderNumber(result.data.orderNumber);
        if (result.data.paymentStatus === "completed") {
          setStatus("confirmed");
          clearCheckoutSession();
          return true;
        }
      }
    } catch {
      // keep polling
    }
    return false;
  }, [token, tokenFromUrl]);

  useEffect(() => {
    const payment = getPaymentResponse();
    const pending = getCheckoutPending();
    const resolvedToken =
      tokenFromUrl || payment?.trackingToken || pending?.trackingToken;

    if (!tokenFromUrl && resolvedToken) {
      navigate(`/checkout/pending?token=${resolvedToken}`, { replace: true });
      return;
    }

    if (!resolvedToken && !payment) {
      setStatus("no_token");
      return;
    }

    let cancelled = false;
    let intervalId;

    const runPoll = async () => {
      const verified = await retryVerifyFromSession();
      if (cancelled || verified) return;

      const done = await checkStatus();
      if (cancelled || done) return;

      setPollCount((c) => {
        const next = c + 1;
        if (next >= MAX_POLLS) {
          setStatus("timeout");
          clearInterval(intervalId);
        }
        return next;
      });
    };

    runPoll();
    intervalId = setInterval(runPoll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [tokenFromUrl, navigate, retryVerifyFromSession, checkStatus]);

  useEffect(() => {
    if (status === "confirmed" && token) {
      const timer = setTimeout(() => {
        window.location.href = `/checkout/success?token=${token}&order=${orderNumber || ""}`;
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [status, token, orderNumber]);

  return (
    <>
      <Helmet>
        <title>Confirming Payment | Knots & Krafts</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-16 px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg border border-amber-100 p-8 text-center"
        >
          {status === "confirmed" ? (
            <>
              <BsCheckIcon />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Order Confirmed!
              </h1>
              <p className="text-gray-600">Redirecting you to confirmation...</p>
            </>
          ) : status === "no_token" ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-4">
                Missing tracking information
              </h1>
              <p className="text-gray-600 mb-6">
                Check your email for the order confirmation link, or enter your
                tracking token below.
              </p>
              <Link
                to="/track"
                className="inline-flex px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600"
              >
                Track Order
              </Link>
            </>
          ) : status === "timeout" ? (
            <>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Still confirming your payment
              </h1>
              <p className="text-gray-600 mb-6">
                Your payment was likely received. Our system will confirm it
                automatically — check your email or track your order below.
              </p>
              {token && (
                <Link
                  to={`/track/${token}`}
                  className="inline-flex px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600"
                >
                  Check Order Status
                </Link>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-center mb-6">
                <AiOutlineLoading3Quarters className="text-4xl text-amber-500 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Confirming your payment
              </h1>
              <p className="text-gray-600 mb-2">
                Please wait — do not close this page.
              </p>
              <p className="text-sm text-gray-500 mb-1">{lastAction}</p>
              <p className="text-sm text-gray-400">
                Attempt {pollCount}/{MAX_POLLS}
              </p>
            </>
          )}
        </motion.div>
      </div>
    </>
  );
};

function BsCheckIcon() {
  return (
    <div className="flex justify-center mb-6">
      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-3xl text-green-600">
        ✓
      </div>
    </div>
  );
}

export default CheckoutPendingPage;
